import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { setPageTitle } from "../../../Store/modules/mms/actions";
import { useAuth } from "../../../Contexts/AuthProvider";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import {
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Tab,
  Tabs,
  Checkbox,
} from "@material-ui/core";
import Fade from "@material-ui/core/Fade";
import TablePagination from "../../PredictiveMaintenance/Equipment/TablePagination";
import SearchInput from "../../../Components/SearchInput";
import DmsService from "../../../Services/DMS/dms-api.service";
import MyIcons from "../../../assets/img/Icons/MyIcons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parse, isValid } from "date-fns";
import { toast } from "react-toastify";
import JsonEditorModal from "../../../Components/modals/JsonEditorModal/JsonEditorModal";
import { useUsersSettings } from "../UsersSettingsContext";

const RemoveIcon = () => <div className="removeIcon" />;

const SIDEBAR_TABS = [
  { id: "tables", name: "Tables" },
  { id: "clients", name: "Clients" },
  { id: "fleets", name: "Fleets" },
  { id: "crews", name: "Crews" },
  { id: "areas", name: "Areas" },
  { id: "pads", name: "Pads" },
];

const CATALOG_SAVE_LABELS = {
  tables: { singular: "Table", plural: "Tables" },
  clients: { singular: "Client", plural: "Clients" },
  fleets: { singular: "Fleet", plural: "Fleets" },
  crews: { singular: "Crew", plural: "Crews" },
  areas: { singular: "Area", plural: "Areas" },
  pads: { singular: "Pad", plural: "Pads" },
};

const formatSaveCountLabel = (count, singular, plural) =>
  count === 1 ? singular : `${count} ${plural}`;

const buildCatalogSaveSuccessMessage = (
  entityKey,
  { created = 0, updated = 0, createdWells = 0, updatedWells = 0 } = {},
) => {
  const labels = CATALOG_SAVE_LABELS[entityKey] || {
    singular: "Record",
    plural: "Records",
  };

  if (entityKey === "pads") {
    const parts = [];
    if (created > 0) {
      parts.push(
        `${formatSaveCountLabel(created, labels.singular, labels.plural)} created`,
      );
    }
    if (updated > 0) {
      parts.push(
        `${formatSaveCountLabel(updated, labels.singular, labels.plural)} updated`,
      );
    }
    if (createdWells > 0) {
      parts.push(
        `${formatSaveCountLabel(createdWells, "Well", "Wells")} created`,
      );
    }
    if (updatedWells > 0) {
      parts.push(
        `${formatSaveCountLabel(updatedWells, "Well", "Wells")} updated`,
      );
    }
    if (parts.length === 0) return "Changes saved successfully.";
    return `${parts.join(", ")} successfully.`;
  }

  if (created > 0 && updated > 0) {
    return `${labels.plural} saved successfully.`;
  }
  if (created > 0) {
    return `${formatSaveCountLabel(created, labels.singular, labels.plural)} created successfully.`;
  }
  if (updated > 0) {
    return `${formatSaveCountLabel(updated, labels.singular, labels.plural)} updated successfully.`;
  }
  return "Changes saved successfully.";
};

const DEFAULT_NEW_ROW_BY_ENTITY = {
  tables: {
    name: "",
    keepLocalCopy: "true",
    showInDms: "true",
    readWrite: "true",
  },
  clients: {
    name: "",
    addressStateProvince: "",
    addressCity: "",
    addressLine1: "",
    addressLine2: "",
    addressPostalCode: "",
  },
  fleets: { name: "" },
  crews: { name: "", clientId: "" },
  areas: { name: "" },
  pads: {
    areaId: "",
    name: "",
  },
  wells: {
    padId: "",
    name: "",
    apiNumber: "",
    latitude: "",
    longitude: "",
    surveysJson: "{}",
    stages: "",
  },
};

const isEmptyParametersJson = (value) => {
  if (value == null) return true;
  if (typeof value === "object") {
    if (Array.isArray(value)) return value.length === 0;
    return Object.keys(value).length === 0;
  }
  const text = String(value).trim();
  if (text === "") return true;
  try {
    const parsed = JSON.parse(text);
    if (parsed == null) return true;
    if (typeof parsed === "object" && !Array.isArray(parsed)) {
      return Object.keys(parsed).length === 0;
    }
    if (typeof parsed === "string") {
      const inner = parsed.trim();
      if (inner === "" || inner === "{}") return true;
      try {
        const innerParsed = JSON.parse(inner);
        return (
          innerParsed &&
          typeof innerParsed === "object" &&
          !Array.isArray(innerParsed) &&
          Object.keys(innerParsed).length === 0
        );
      } catch {
        return false;
      }
    }
    return false;
  } catch {
    return text === "{}";
  }
};

const formatJsonPreview = (value) => {
  if (isEmptyParametersJson(value)) return "";
  if (typeof value === "object") {
    try {
      const compact = JSON.stringify(value);
      if (compact.length <= 48) return compact;
      return `${compact.slice(0, 45)}...`;
    } catch {
      return "";
    }
  }
  const text = String(value);
  try {
    const compact = JSON.stringify(JSON.parse(text));
    if (compact.length <= 48) return compact;
    return `${compact.slice(0, 45)}...`;
  } catch {
    if (text.length <= 48) return text;
    return `${text.slice(0, 45)}...`;
  }
};

const normalizeCatalogResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
};

const getCatalogTotalCount = (data, rowsLength) => {
  if (data && typeof data.totalCount === "number") return data.totalCount;
  if (data && typeof data.total === "number") return data.total;
  return rowsLength;
};

const isBooleanCell = (value) => {
  const v = String(value ?? "").toLowerCase();
  return v === "true" || v === "false";
};

const BOOLEAN_NORMALIZED = ["keeplocalcopy", "showindms", "readwrite"];

const CHECKBOX_COLUMNS = ["keepLocalCopy", "showInDms"];

const isCheckboxColumn = (col) => CHECKBOX_COLUMNS.includes(col?.key);

const isTruthyBooleanValue = (value) => {
  const v = String(value ?? "").toLowerCase();
  return v === "true";
};

const normKey = (s) => (s || "").toLowerCase().replace(/[\s_]/g, "");

const isBooleanColumn = (col) => {
  const keyNorm = normKey(col?.key);
  const labelNorm = normKey(col?.label);
  return BOOLEAN_NORMALIZED.some(
    (b) =>
      keyNorm === b ||
      labelNorm === b ||
      keyNorm.includes(b) ||
      labelNorm.includes(b),
  );
};

const isBooleanKey = (key) =>
  BOOLEAN_NORMALIZED.some(
    (b) => normKey(key) === b || normKey(key).includes(b),
  );

const COLUMN_LABEL_OVERRIDE = {
  crewId: "Crew",
  clientId: "Client",
  areaId: "Area",
  padId: "Pad",
  parametersJson: "Parameters Json",
  comments: "Comments",
};

const CREATION_DATE_FORMAT = "yyyy-MM-dd";

const GLOBAL_READ_ONLY_COLUMNS = ["updatedAt", "updated_at"];

const READ_ONLY_COLUMNS_BY_ENTITY = {
  tables: ["name"],
};

const JOB_CONFIGURATION_CELL_DISPLAY_CLASS =
  "jobConfigurationCellValue jobConfigurationCellValueEditable";

const UPSERT_BATCH_ENTITIES = new Set([
  "pads",
  "clients",
  "fleets",
  "crews",
  "areas",
]);

const usesBatchUpsert = (entity) => UPSERT_BATCH_ENTITIES.has(entity);

const isReadOnlyColumn = (entity, columnKey) =>
  GLOBAL_READ_ONLY_COLUMNS.includes(columnKey) ||
  (READ_ONLY_COLUMNS_BY_ENTITY[entity] || []).includes(columnKey);

const normalizeCellValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const hasCellValueChanged = (previous, next) =>
  normalizeCellValue(previous) !== normalizeCellValue(next);

const COLUMN_ORDER_BY_ENTITY = {
  tables: ["name", "keepLocalCopy", "showInDms", "readWrite"],
  clients: ["name"],
  fleets: ["name"],
  crews: ["name", "clientId"],
  areas: ["name"],
  pads: ["name", "areaId", "updatedAt"],
};

const PAD_INNER_WELL_COLUMNS = [
  { key: "name", label: "Well Name" },
  { key: "apiNumber", label: "Api Number" },
  { key: "latitude", label: "Latitude" },
  { key: "longitude", label: "Longitude" },
  { key: "stages", label: "Stages" },
];

const normalizePadsWithIncludesResponse = (data) => {
  const items = data?.items ?? [];
  return items.map((item, index) => {
    const pad = item?.pad ?? {};
    const wells = (item?.wells ?? []).map((well, wellIndex) => ({
      ...well,
      id: well.id ?? `well-${index}-${wellIndex}`,
      padId: well.padId ?? pad.id,
    }));
    return {
      ...pad,
      id: pad.id ?? `pads-row-${index}`,
      _wells: wells,
    };
  });
};

const rowToPayload = (row, entity) => {
  const payload = { ...row };
  delete payload.id;
  delete payload._wells;
  Object.keys(payload).forEach((key) => {
    const v = payload[key];
    if (v === "true" || v === true) payload[key] = true;
    else if (v === "false" || v === false) payload[key] = false;
  });
  return payload;
};

const wellToPayload = (well) => {
  const payload = rowToPayload(well, "wells");
  delete payload.padId;
  if (well.id && !String(well.id).startsWith("new-")) {
    payload.id = well.id;
  }
  if (payload.stages !== undefined && payload.stages !== "") {
    const stagesNum = Number(payload.stages);
    if (!Number.isNaN(stagesNum)) payload.stages = stagesNum;
  }
  return payload;
};

const padWithIncludesToUpsertPayload = (pad, wells) => ({
  ...entityRowToUpsertPayload(pad, "pads"),
  wells,
});

const entityRowToUpsertPayload = (row, entity) => {
  const payload = rowToPayload(row, entity);
  if (row.id && !String(row.id).startsWith("new-")) {
    payload.id = row.id;
  }
  return payload;
};

const createNewRow = (entity) => {
  const defaults = DEFAULT_NEW_ROW_BY_ENTITY[entity] || {};
  return {
    id: `new-${entity}-${Date.now()}`,
    ...defaults,
  };
};

const JobConfiguration = () => {
  const { currentUser } = useAuth();
  const { setPageHeaderActions } = useUsersSettings();
  const dispatch = useDispatch();
  const currentCompany = useSelector((state) => state.dashboard?.currentCompany);

  useEffect(() => {
    dispatch(setPageTitle("Catalog"));
  }, [dispatch]);

  const isSuperUser = useMemo(
    () => Boolean(currentUser?.isSuperuser?.()),
    [currentUser],
  );

  const canViewCatalogTablesTab = useMemo(
    () =>
      Boolean(
        currentUser?.isSuperuser?.() || currentUser?.isMaster?.(),
      ),
    [currentUser],
  );

  const visibleTabs = useMemo(
    () =>
      SIDEBAR_TABS.filter(
        (tab) => tab.id !== "tables" || canViewCatalogTablesTab,
      ),
    [canViewCatalogTablesTab],
  );

  const [selectedDocumentId, setSelectedDocumentId] = useState(() => {
    const canSeeTables =
      currentUser?.isSuperuser?.() || currentUser?.isMaster?.();
    return canSeeTables ? SIDEBAR_TABS[0].id : (SIDEBAR_TABS.find((t) => t.id !== "tables")?.id ?? SIDEBAR_TABS[0].id);
  });
  const [tableDataByEntity, setTableDataByEntity] = useState({});
  const [catalogTotalCount, setCatalogTotalCount] = useState({});
  const [newRowsByEntity, setNewRowsByEntity] = useState({});
  const [editsByEntity, setEditsByEntity] = useState({});
  const [editsWellsById, setEditsWellsById] = useState({});
  const [catalogCrews, setCatalogCrews] = useState([]);
  const [catalogClients, setCatalogClients] = useState([]);
  const [catalogAreas, setCatalogAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [booleanPopover, setBooleanPopover] = useState({
    anchorEl: null,
    rowId: null,
    columnKey: null,
  });
  const [areaPopover, setAreaPopover] = useState({
    anchorEl: null,
    rowId: null,
  });
  const [crewPopover, setCrewPopover] = useState({
    anchorEl: null,
    rowId: null,
  });
  const [clientPopover, setClientPopover] = useState({
    anchorEl: null,
    rowId: null,
  });
  const [areaPopoverForPad, setAreaPopoverForPad] = useState({
    anchorEl: null,
    rowId: null,
  });
  const [datePickerPopover, setDatePickerPopover] = useState({
    anchorEl: null,
    rowId: null,
  });
  const [saving, setSaving] = useState(false);
  const [catalogMaintenanceAction, setCatalogMaintenanceAction] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [parametersJsonModal, setParametersJsonModal] = useState({
    open: false,
    rowId: null,
  });
  const [editingCell, setEditingCell] = useState({
    rowId: null,
    columnKey: null,
    originalValue: null,
  });
  const [expandedPadIds, setExpandedPadIds] = useState([]);
  const [newWellsByPadId, setNewWellsByPadId] = useState({});

  const entity = selectedDocumentId;

  useEffect(() => {
    setEditingCell({ rowId: null, columnKey: null, originalValue: null });
  }, [entity]);

  useEffect(() => {
    if (!visibleTabs.length) return;
    setSelectedDocumentId((current) => {
      if (visibleTabs.some((tab) => tab.id === current)) return current;
      return visibleTabs[0].id;
    });
  }, [visibleTabs]);

  const openBooleanPopover = useCallback((event, rowId, columnKey) => {
    event.stopPropagation();
    setBooleanPopover({ anchorEl: event.currentTarget, rowId, columnKey });
  }, []);

  const closeBooleanPopover = useCallback(() => {
    setBooleanPopover({ anchorEl: null, rowId: null, columnKey: null });
  }, []);

  const openAreaPopover = useCallback((event, rowId) => {
    event.stopPropagation();
    setAreaPopover({ anchorEl: event.currentTarget, rowId });
  }, []);

  const closeAreaPopover = useCallback(() => {
    setAreaPopover({ anchorEl: null, rowId: null });
  }, []);

  const openCrewPopover = useCallback((event, rowId) => {
    event.stopPropagation();
    setCrewPopover({ anchorEl: event.currentTarget, rowId });
  }, []);

  const closeCrewPopover = useCallback(() => {
    setCrewPopover({ anchorEl: null, rowId: null });
  }, []);

  const openClientPopover = useCallback((event, rowId) => {
    event.stopPropagation();
    setClientPopover({ anchorEl: event.currentTarget, rowId });
  }, []);

  const closeClientPopover = useCallback(() => {
    setClientPopover({ anchorEl: null, rowId: null });
  }, []);

  const openAreaPopoverForPad = useCallback((event, rowId) => {
    event.stopPropagation();
    setAreaPopoverForPad({ anchorEl: event.currentTarget, rowId });
  }, []);

  const closeAreaPopoverForPad = useCallback(() => {
    setAreaPopoverForPad({ anchorEl: null, rowId: null });
  }, []);

  const openDatePickerPopover = useCallback((event, rowId) => {
    event.stopPropagation();
    setDatePickerPopover({ anchorEl: event.currentTarget, rowId });
  }, []);

  const closeDatePickerPopover = useCallback(() => {
    setDatePickerPopover({ anchorEl: null, rowId: null });
  }, []);

  const openParametersJsonModal = useCallback((event, rowId) => {
    event.stopPropagation();
    setParametersJsonModal({ open: true, rowId });
  }, []);

  const closeParametersJsonModal = useCallback(() => {
    setParametersJsonModal({ open: false, rowId: null });
  }, []);

  const updateRowOnServer = useCallback(
    async (row, catalogEntity = entity) => {
      if (usesBatchUpsert(catalogEntity)) return;
      if (String(row?.id || "").startsWith("new-")) return;
      try {
        const payload = { ...rowToPayload(row, catalogEntity), id: row.id };
        await DmsService.putCatalog(catalogEntity, [payload], currentCompany?.id);
      } catch (err) {
        setFetchError(
          err?.response?.data?.message ?? err?.message ?? "Failed to update",
        );
      }
    },
    [entity, currentCompany],
  );

  const getOriginalEntityRow = useCallback(
    (entityKey, rowId) =>
      (tableDataByEntity[entityKey] || []).find((row) => row.id === rowId),
    [tableDataByEntity],
  );

  const getOriginalWell = useCallback(
    (wellId) => {
      for (const pad of tableDataByEntity.pads || []) {
        const well = (pad._wells || []).find((w) => w.id === wellId);
        if (well) return well;
      }
      return null;
    },
    [tableDataByEntity],
  );

  const mergeEntityRow = useCallback(
    (entityKey, row) => {
      const edits = editsByEntity[entityKey]?.[row.id];
      if (!edits) return row;
      return { ...row, ...edits };
    },
    [editsByEntity],
  );

  const mergeWellRow = useCallback(
    (well) => {
      const edits = editsWellsById[well.id];
      if (!edits) return well;
      return { ...well, ...edits };
    },
    [editsWellsById],
  );

  const applyEntityEdit = useCallback(
    (entityKey, rowId, patch) => {
      const isNew = String(rowId).startsWith("new-");
      if (isNew) {
        setNewRowsByEntity((prev) => ({
          ...prev,
          [entityKey]: (prev[entityKey] || []).map((row) =>
            row.id === rowId ? { ...row, ...patch } : row,
          ),
        }));
        return;
      }

      setEditsByEntity((prev) => {
        const current = prev[entityKey]?.[rowId] || {};
        const next = { ...current, ...patch };
        const original = (tableDataByEntity[entityKey] || []).find((r) => r.id === rowId);
        if (!original) return prev;

        const merged = { ...original, ...next };
        const unchanged = Object.keys(next).every(
          (key) => !hasCellValueChanged(original[key], merged[key]),
        );

        if (unchanged) {
          const entityEdits = { ...(prev[entityKey] || {}) };
          delete entityEdits[rowId];
          if (Object.keys(entityEdits).length === 0) {
            const { [entityKey]: _, ...rest } = prev;
            return rest;
          }
          return { ...prev, [entityKey]: entityEdits };
        }
        return {
          ...prev,
          [entityKey]: { ...(prev[entityKey] || {}), [rowId]: next },
        };
      });
    },
    [tableDataByEntity],
  );

  const applyWellEdit = useCallback(
    (wellId, patch) => {
      const isNew = String(wellId).startsWith("new-");
      if (isNew) {
        setNewWellsByPadId((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((padId) => {
            next[padId] = (next[padId] || []).map((well) =>
              well.id === wellId ? { ...well, ...patch } : well,
            );
          });
          return next;
        });
        return;
      }

      setEditsWellsById((prev) => {
        const current = prev[wellId] || {};
        const next = { ...current, ...patch };
        const original = getOriginalWell(wellId);
        if (!original) return prev;

        const merged = { ...original, ...next };
        const unchanged = Object.keys(next).every(
          (key) => !hasCellValueChanged(original[key], merged[key]),
        );

        if (unchanged) {
          const { [wellId]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [wellId]: next };
      });
    },
    [getOriginalWell],
  );

  const isEntityFieldChanged = useCallback(
    (entityKey, row, columnKey) => {
      const isNew = String(row.id).startsWith("new-");
      if (isNew) {
        const defaults = DEFAULT_NEW_ROW_BY_ENTITY[entityKey] || {};
        const defaultVal = defaults[columnKey];
        const currentVal = row[columnKey];
        if (defaultVal === undefined) return normalizeCellValue(currentVal) !== "";
        return hasCellValueChanged(defaultVal, currentVal);
      }
      const original = getOriginalEntityRow(entityKey, row.id);
      if (!original || !editsByEntity[entityKey]?.[row.id]) return false;
      if (!(columnKey in editsByEntity[entityKey][row.id])) return false;
      return hasCellValueChanged(original[columnKey], row[columnKey]);
    },
    [editsByEntity, getOriginalEntityRow],
  );

  const isWellFieldChanged = useCallback(
    (well, columnKey) => {
      const isNew = String(well.id).startsWith("new-");
      if (isNew) {
        const defaults = DEFAULT_NEW_ROW_BY_ENTITY.wells || {};
        const defaultVal = defaults[columnKey];
        const currentVal = well[columnKey];
        if (defaultVal === undefined) return normalizeCellValue(currentVal) !== "";
        return hasCellValueChanged(defaultVal, currentVal);
      }
      const original = getOriginalWell(well.id);
      if (!original || !editsWellsById[well.id]) return false;
      if (!(columnKey in editsWellsById[well.id])) return false;
      return hasCellValueChanged(original[columnKey], well[columnKey]);
    },
    [editsWellsById, getOriginalWell],
  );

  const getCellBorderClass = useCallback(
    (entityKey, row, columnKey, rowKind = "entity") => {
      if (!usesBatchUpsert(entityKey)) return "";
      const changed =
        rowKind === "well"
          ? isWellFieldChanged(row, columnKey)
          : isEntityFieldChanged(entityKey, row, columnKey);
      if (!changed) return "";
      return saving ? "jobConfigurationCellSaving" : "jobConfigurationCellChanged";
    },
    [isEntityFieldChanged, isWellFieldChanged, saving],
  );

  const getCellDisplayClass = useCallback(
    (entityKey, row, columnKey, rowKind = "entity") =>
      `${JOB_CONFIGURATION_CELL_DISPLAY_CLASS} ${getCellBorderClass(entityKey, row, columnKey, rowKind)}`.trim(),
    [getCellBorderClass],
  );

  const getWellById = useCallback(
    (wellId) => {
      for (const padId of Object.keys(newWellsByPadId)) {
        const well = (newWellsByPadId[padId] || []).find((w) => w.id === wellId);
        if (well) return mergeWellRow(well);
      }
      for (const pad of tableDataByEntity.pads || []) {
        const well = (pad._wells || []).find((w) => w.id === wellId);
        if (well) return mergeWellRow(well);
      }
      return null;
    },
    [tableDataByEntity, newWellsByPadId, mergeWellRow],
  );

  const getRowById = useCallback(
    (rowId) => {
      const newRows = newRowsByEntity[entity] || [];
      const fromNew = newRows.find((row) => row.id === rowId);
      if (fromNew) return fromNew;
      const serverRow = (tableDataByEntity[entity] || []).find((row) => row.id === rowId);
      if (serverRow) return mergeEntityRow(entity, serverRow);
      if (entity === "pads") return getWellById(rowId);
      return null;
    },
    [entity, newRowsByEntity, tableDataByEntity, getWellById, mergeEntityRow],
  );

  const parametersJsonModalValue = useMemo(() => {
    if (!parametersJsonModal.open || parametersJsonModal.rowId == null) {
      return "";
    }
    const row = getRowById(parametersJsonModal.rowId);
    const value = row?.parametersJson;
    return isEmptyParametersJson(value) ? "" : String(value ?? "");
  }, [parametersJsonModal, getRowById]);

  const updateWellField = useCallback(
    (wellId, columnKey, value, persist = false) => {
      applyWellEdit(wellId, { [columnKey]: value });
      if (persist) {
        const well = getWellById(wellId);
        if (well) updateRowOnServer(well, "wells");
      }
      return getWellById(wellId);
    },
    [applyWellEdit, getWellById, updateRowOnServer],
  );

  const togglePadExpanded = useCallback((padId) => {
    setExpandedPadIds((prev) =>
      prev.includes(padId)
        ? prev.filter((id) => id !== padId)
        : [...prev, padId],
    );
  }, []);

  const handleAddWellToPad = useCallback((padId, event) => {
    event?.stopPropagation?.();
    const newWell = {
      id: `new-well-${padId}-${Date.now()}`,
      ...DEFAULT_NEW_ROW_BY_ENTITY.wells,
      padId,
    };
    setNewWellsByPadId((prev) => ({
      ...prev,
      [padId]: [...(prev[padId] || []), newWell],
    }));
    setExpandedPadIds((prev) =>
      prev.includes(padId) ? prev : [...prev, padId],
    );
  }, []);

  const applyBooleanCellValue = useCallback(
    (rowId, columnKey, value) => {
      if (usesBatchUpsert(selectedDocumentId)) {
        applyEntityEdit(selectedDocumentId, rowId, { [columnKey]: value });
        return;
      }

      const isNew = String(rowId).startsWith("new-");
      if (isNew) {
        setNewRowsByEntity((prev) => ({
          ...prev,
          [selectedDocumentId]: (prev[selectedDocumentId] || []).map((row) =>
            row.id === rowId ? { ...row, [columnKey]: value } : row,
          ),
        }));
        return;
      }

      const sourceRow = (tableDataByEntity[selectedDocumentId] || []).find(
        (row) => row.id === rowId,
      );
      if (!sourceRow || !hasCellValueChanged(sourceRow[columnKey], value)) {
        return;
      }

      let updatedRow = null;
      setTableDataByEntity((prev) => {
        const next = {
          ...prev,
          [selectedDocumentId]: (prev[selectedDocumentId] || []).map((row) => {
            if (row.id === rowId) {
              updatedRow = { ...row, [columnKey]: value };
              return updatedRow;
            }
            return row;
          }),
        };
        return next;
      });
      if (updatedRow) {
        updateRowOnServer(updatedRow);
      }
    },
    [selectedDocumentId, tableDataByEntity, updateRowOnServer, applyEntityEdit],
  );

  const selectBooleanValue = useCallback(
    (value) => {
      const { rowId, columnKey } = booleanPopover;
      if (rowId == null || columnKey == null) {
        closeBooleanPopover();
        return;
      }
      applyBooleanCellValue(rowId, columnKey, value);
      closeBooleanPopover();
    },
    [booleanPopover, closeBooleanPopover, applyBooleanCellValue],
  );

  const handleCheckboxChange = useCallback(
    (row, columnKey, checked) => {
      const value = checked ? "true" : "false";
      applyBooleanCellValue(row.id, columnKey, value);
    },
    [applyBooleanCellValue],
  );

  const applyCatalogFieldChange = useCallback(
    (entityKey, rowId, patch, { persist = false } = {}) => {
      if (usesBatchUpsert(entityKey)) {
        applyEntityEdit(entityKey, rowId, patch);
        return;
      }

      const isNew = String(rowId).startsWith("new-");
      if (isNew) {
        setNewRowsByEntity((prev) => ({
          ...prev,
          [entityKey]: (prev[entityKey] || []).map((row) =>
            row.id === rowId ? { ...row, ...patch } : row,
          ),
        }));
        return;
      }

      let updatedRow = null;
      setTableDataByEntity((prev) => ({
        ...prev,
        [entityKey]: (prev[entityKey] || []).map((row) => {
          if (row.id === rowId) {
            updatedRow = { ...row, ...patch };
            return updatedRow;
          }
          return row;
        }),
      }));
      if (persist && updatedRow) {
        updateRowOnServer(updatedRow, entityKey);
      }
    },
    [applyEntityEdit, updateRowOnServer],
  );

  const selectArea = useCallback(
    (area) => {
      const { rowId } = areaPopover;
      if (rowId == null) {
        closeAreaPopover();
        return;
      }
      applyCatalogFieldChange(selectedDocumentId, rowId, {
        areaId: area ? area.id : "",
      });
      closeAreaPopover();
    },
    [areaPopover, selectedDocumentId, closeAreaPopover, applyCatalogFieldChange],
  );

  const selectCrew = useCallback(
    (crew) => {
      const { rowId } = crewPopover;
      if (rowId == null) {
        closeCrewPopover();
        return;
      }
      applyCatalogFieldChange(selectedDocumentId, rowId, {
        crewId: crew ? crew.id : "",
      });
      closeCrewPopover();
    },
    [crewPopover, selectedDocumentId, closeCrewPopover, applyCatalogFieldChange],
  );

  const selectClient = useCallback(
    (client) => {
      const { rowId } = clientPopover;
      if (rowId == null) {
        closeClientPopover();
        return;
      }
      applyCatalogFieldChange(selectedDocumentId, rowId, {
        clientId: client ? client.id : "",
      });
      closeClientPopover();
    },
    [clientPopover, selectedDocumentId, closeClientPopover, applyCatalogFieldChange],
  );

  const selectAreaForPad = useCallback(
    (area) => {
      const { rowId } = areaPopoverForPad;
      if (rowId == null) {
        closeAreaPopoverForPad();
        return;
      }
      applyCatalogFieldChange("pads", rowId, {
        areaId: area ? area.id : "",
      });
      closeAreaPopoverForPad();
    },
    [areaPopoverForPad, closeAreaPopoverForPad, applyCatalogFieldChange],
  );

  const handleCreationDateChange = useCallback(
    (date) => {
      const { rowId } = datePickerPopover;
      if (rowId == null) {
        closeDatePickerPopover();
        return;
      }
      const value = date ? format(date, CREATION_DATE_FORMAT) : "";
      applyCatalogFieldChange(selectedDocumentId, rowId, {
        creationDate: value,
      });
      closeDatePickerPopover();
    },
    [
      datePickerPopover,
      selectedDocumentId,
      closeDatePickerPopover,
      applyCatalogFieldChange,
    ],
  );

  useEffect(() => {
    // For superuser, wait for currentCompany so CompanyId header is set (avoids 400 on refresh)
    if (currentUser?.isSuperuser?.() && !currentCompany) return;

    let cancelled = false;
    const fetchCatalog = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const response =
          entity === "pads"
            ? await DmsService.getCatalogPadsWithIncludes(
                pageNumber,
                pageSize,
                currentCompany?.id,
                searchTerm,
              )
            : await DmsService.getCatalog(
                entity,
                pageNumber,
                pageSize,
                currentCompany?.id,
                searchTerm,
              );
        const rowsWithId =
          entity === "pads"
            ? normalizePadsWithIncludesResponse(response.data)
            : (() => {
                const rows = normalizeCatalogResponse(response.data);
                return Array.isArray(rows)
                  ? rows.map((row, index) => ({
                      ...row,
                      id: row.id ?? `${entity}-row-${index}`,
                    }))
                  : [];
              })();
        const total = getCatalogTotalCount(response.data, rowsWithId.length);
        if (!cancelled) {
          setTableDataByEntity((prev) => ({ ...prev, [entity]: rowsWithId }));
          setCatalogTotalCount((prev) => ({ ...prev, [entity]: total }));
        }
      } catch (err) {
        if (!cancelled) {
          setFetchError(
            err?.response?.data?.message ?? err?.message ?? "Failed to load",
          );
          setTableDataByEntity((prev) => ({ ...prev, [entity]: [] }));
          setCatalogTotalCount((prev) => ({ ...prev, [entity]: 0 }));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchCatalog();
    return () => {
      cancelled = true;
    };
  }, [entity, pageNumber, pageSize, refreshKey, searchTerm, currentUser, currentCompany]);

  useEffect(() => {
    if (currentUser?.isSuperuser?.() && !currentCompany) return;
    if (entity !== "crews") return;
    let cancelled = false;
    DmsService.getCatalog("clients", 1, 500, currentCompany?.id)
      .then((res) => {
        const rows = normalizeCatalogResponse(res.data);
        const list = Array.isArray(rows)
          ? rows.map((row, i) => ({ id: row.id ?? i, name: row.name ?? "" }))
          : [];
        if (!cancelled) setCatalogClients(list);
      })
      .catch(() => {
        if (!cancelled) setCatalogClients([]);
      });
    return () => {
      cancelled = true;
    };
  }, [entity, currentUser, currentCompany]);

  useEffect(() => {
    if (currentUser?.isSuperuser?.() && !currentCompany) return;
    if (entity !== "pads") return;
    let cancelled = false;
    DmsService.getCatalog("areas", 1, 500, currentCompany?.id)
      .then((res) => {
        const rows = normalizeCatalogResponse(res.data);
        const list = Array.isArray(rows)
          ? rows.map((row, i) => ({ id: row.id ?? i, name: row.name ?? "" }))
          : [];
        if (!cancelled) setCatalogAreas(list);
      })
      .catch(() => {
        if (!cancelled) setCatalogAreas([]);
      });
    return () => {
      cancelled = true;
    };
  }, [entity, currentUser, currentCompany]);

  const handleAddRow = useCallback(() => {
    const serverRows = tableDataByEntity[entity] ?? [];
    const newRows = newRowsByEntity[entity] ?? [];
    const existingKeys =
      serverRows.length > 0
        ? Object.keys(serverRows[0]).filter((k) => k !== "id" && k !== "_wells")
        : newRows.length > 0
          ? Object.keys(newRows[0]).filter((k) => k !== "id" && k !== "_wells")
          : [];
    const entityDefaults = DEFAULT_NEW_ROW_BY_ENTITY[entity] || {};
    const newRow =
      existingKeys.length > 0
        ? {
            id: `new-${entity}-${Date.now()}`,
            ...Object.fromEntries(
              existingKeys.map((k) => [
                k,
                entityDefaults[k] !== undefined
                  ? entityDefaults[k]
                  : isBooleanKey(k)
                    ? "true"
                    : "",
              ]),
            ),
          }
        : createNewRow(entity);
    setNewRowsByEntity((prev) => ({
      ...prev,
      [entity]: [newRow, ...(prev[entity] || [])],
    }));
    setPageNumber(1);
  }, [entity, tableDataByEntity, newRowsByEntity]);

  const selectedDocument = useMemo(
    () => visibleTabs.find((d) => d.id === selectedDocumentId),
    [selectedDocumentId, visibleTabs],
  );

  const datePickerSelectedDate = useMemo(() => {
    return null;
  }, []);

  const rows = useMemo(() => {
    const serverRows = (tableDataByEntity[selectedDocumentId] ?? []).map((row) =>
      mergeEntityRow(selectedDocumentId, row),
    );
    const newRows = newRowsByEntity[selectedDocumentId] ?? [];
    if (pageNumber === 1 && newRows.length > 0) {
      return [...newRows, ...serverRows].slice(0, pageSize);
    }
    return serverRows;
  }, [
    tableDataByEntity,
    newRowsByEntity,
    selectedDocumentId,
    pageNumber,
    pageSize,
    mergeEntityRow,
  ]);

  const hasPendingPadWells = useMemo(
    () =>
      Object.values(newWellsByPadId).some(
        (wells) => Array.isArray(wells) && wells.length > 0,
      ),
    [newWellsByPadId],
  );

  const hasPendingWellEdits = useMemo(
    () => Object.keys(editsWellsById).length > 0,
    [editsWellsById],
  );

  const hasPendingEntityEdits = useMemo(
    () => Object.keys(editsByEntity[entity] || {}).length > 0,
    [editsByEntity, entity],
  );

  const hasPendingChanges = useMemo(() => {
    const hasNewRows = (newRowsByEntity[entity]?.length ?? 0) > 0;
    if (entity === "tables") {
      return hasNewRows;
    }
    if (entity === "pads") {
      return hasNewRows || hasPendingEntityEdits || hasPendingPadWells || hasPendingWellEdits;
    }
    if (usesBatchUpsert(entity)) {
      return hasNewRows || hasPendingEntityEdits;
    }
    return hasNewRows;
  }, [
    entity,
    newRowsByEntity,
    hasPendingEntityEdits,
    hasPendingPadWells,
    hasPendingWellEdits,
  ]);

  const columns = useMemo(() => {
    let keys =
      entity === "pads"
        ? [...(COLUMN_ORDER_BY_ENTITY.pads || [])]
        : rows.length > 0
          ? [
              ...new Set(
                rows.flatMap((row) =>
                  Object.keys(row).filter(
                    (k) => k !== "id" && k !== "_wells",
                  ),
                ),
              ),
            ]
          : Object.keys(DEFAULT_NEW_ROW_BY_ENTITY[entity] || {});
    const order = COLUMN_ORDER_BY_ENTITY[entity];
    if (order && order.length > 0) {
      keys = [...keys].sort((a, b) => {
        const i = order.indexOf(a);
        const j = order.indexOf(b);
        if (i !== -1 && j !== -1) return i - j;
        if (i !== -1) return -1;
        if (j !== -1) return 1;
        return 0;
      });
    }
    return keys.map((key) => ({
      key,
      label:
        COLUMN_LABEL_OVERRIDE[key] ??
        key.charAt(0).toUpperCase() +
          key
            .slice(1)
            .replace(/([A-Z])/g, " $1")
            .trim(),
    }));
  }, [rows, entity]);

  const totalCount =
    (catalogTotalCount[selectedDocumentId] ?? 0) +
    (newRowsByEntity[selectedDocumentId]?.length ?? 0);
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginatedRows = rows;

  const pagination = useMemo(
    () => ({
      pageSize,
      pageNumber,
      itemsCount: paginatedRows.length,
      itemsTotalCount: totalCount,
      pageCount,
    }),
    [pageSize, pageNumber, paginatedRows.length, totalCount, pageCount],
  );

  const onPrevPage = useCallback(() => {
    setPageNumber((p) => Math.max(1, p - 1));
    window.scrollTo(0, 0);
  }, []);

  const onNextPage = useCallback(() => {
    setPageNumber((p) => Math.min(pageCount, p + 1));
    window.scrollTo(0, 0);
  }, [pageCount]);

  const handleRowCount = useCallback((count) => {
    setPageSize(count);
    setPageNumber(1);
  }, []);

  const handleCellBlur = useCallback(
    (row, columnKey, value, originalValue) => {
      if (!row) return;
      if (String(row.id || "").startsWith("new-")) return;
      if (!hasCellValueChanged(originalValue, value)) return;
      const updatedRow = { ...row, [columnKey]: value };
      updateRowOnServer(updatedRow);
    },
    [updateRowOnServer],
  );

  const updateRowField = useCallback(
    (rowId, columnKey, value, persist = false) => {
      if (usesBatchUpsert(entity)) {
        applyEntityEdit(entity, rowId, { [columnKey]: value });
        if (persist) {
          const row = getRowById(rowId);
          if (row) updateRowOnServer(row);
        }
        return getRowById(rowId);
      }

      const isNew = String(rowId).startsWith("new-");
      if (isNew) {
        setNewRowsByEntity((prev) => ({
          ...prev,
          [entity]: (prev[entity] || []).map((row) =>
            row.id === rowId ? { ...row, [columnKey]: value } : row,
          ),
        }));
        return getRowById(rowId);
      }

      let updatedRow = null;
      setTableDataByEntity((prev) => ({
        ...prev,
        [entity]: (prev[entity] || []).map((row) => {
          if (row.id === rowId) {
            updatedRow = { ...row, [columnKey]: value };
            return updatedRow;
          }
          return row;
        }),
      }));

      if (persist && updatedRow) {
        updateRowOnServer(updatedRow);
      }
      return updatedRow;
    },
    [entity, applyEntityEdit, getRowById, updateRowOnServer],
  );

  const handleParametersJsonSave = useCallback(
    (jsonValue) => {
      const { rowId } = parametersJsonModal;
      if (rowId == null) return;
      updateRowField(rowId, "parametersJson", jsonValue, true);
    },
    [parametersJsonModal, updateRowField],
  );

  const handleCellChange = useCallback(
    (rowId, columnKey, value, rowKind = "entity") => {
      if (rowKind === "well") {
        updateWellField(rowId, columnKey, value, false);
        return;
      }
      if (usesBatchUpsert(selectedDocumentId)) {
        applyEntityEdit(selectedDocumentId, rowId, { [columnKey]: value });
        return;
      }
      const isNew = String(rowId).startsWith("new-");
      if (isNew) {
        setNewRowsByEntity((prev) => ({
          ...prev,
          [selectedDocumentId]: (prev[selectedDocumentId] || []).map((row) =>
            row.id === rowId ? { ...row, [columnKey]: value } : row,
          ),
        }));
      } else {
        setTableDataByEntity((prev) => ({
          ...prev,
          [selectedDocumentId]: (prev[selectedDocumentId] || []).map((row) =>
            row.id === rowId ? { ...row, [columnKey]: value } : row,
          ),
        }));
      }
    },
    [selectedDocumentId, updateWellField, applyEntityEdit],
  );

  const handleRemoveRow = useCallback(
    async (rowId, rowKind = "entity") => {
      const isNewWell = String(rowId).startsWith("new-well-");
      if (rowKind === "well" || isNewWell) {
        if (isNewWell) {
          setNewWellsByPadId((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((padId) => {
              next[padId] = (next[padId] || []).filter((well) => well.id !== rowId);
            });
            return next;
          });
          return;
        }
        setFetchError(null);
        try {
          await DmsService.deleteCatalog("wells", [rowId], currentCompany?.id);
          setTableDataByEntity((prev) => ({
            ...prev,
            pads: (prev.pads || []).map((pad) => ({
              ...pad,
              _wells: (pad._wells || []).filter((well) => well.id !== rowId),
            })),
          }));
        } catch (err) {
          const errorMessage =
            err?.response?.data?.message ?? err?.message ?? "Failed to delete";
          setFetchError(errorMessage);
          toast.error(errorMessage);
        }
        return;
      }

      const isNew = String(rowId).startsWith("new-");
      if (isNew) {
        setNewRowsByEntity((prev) => ({
          ...prev,
          [selectedDocumentId]: (prev[selectedDocumentId] || []).filter(
            (row) => row.id !== rowId,
          ),
        }));
      } else {
        setFetchError(null);
        try {
          await DmsService.deleteCatalog(entity, [rowId], currentCompany?.id);
          setTableDataByEntity((prev) => ({
            ...prev,
            [selectedDocumentId]: (prev[selectedDocumentId] || []).filter(
              (row) => row.id !== rowId,
            ),
          }));
          setCatalogTotalCount((prev) => ({
            ...prev,
            [selectedDocumentId]: Math.max(
              0,
              (prev[selectedDocumentId] ?? 0) - 1,
            ),
          }));
          setExpandedPadIds((prev) => prev.filter((id) => id !== rowId));
          setNewWellsByPadId((prev) => {
            const next = { ...prev };
            delete next[rowId];
            return next;
          });
        } catch (err) {
          const errorMessage =
            err?.response?.data?.message ?? err?.message ?? "Failed to delete";
          setFetchError(errorMessage);
          toast.error(errorMessage);
        }
      }
    },
    [selectedDocumentId, entity, currentCompany],
  );

  const handleCatalogSeed = useCallback(async () => {
    if (currentUser?.isSuperuser?.() && !currentCompany) {
      toast.error("Select a company first.");
      return;
    }
    setCatalogMaintenanceAction("seed");
    setFetchError(null);
    try {
      await DmsService.seedCatalog(currentCompany?.id);
      toast.success("Catalog seeded successfully.");
      setRefreshKey((k) => k + 1);
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ?? err?.message ?? "Failed to seed catalog";
      setFetchError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setCatalogMaintenanceAction(null);
    }
  }, [currentUser, currentCompany]);

  const handleCatalogCleanup = useCallback(async () => {
    if (currentUser?.isSuperuser?.() && !currentCompany) {
      toast.error("Select a company first.");
      return;
    }
    setCatalogMaintenanceAction("cleanup");
    setFetchError(null);
    try {
      await DmsService.cleanupCatalog(currentCompany?.id);
      toast.success("Catalog cleanup completed successfully.");
      setRefreshKey((k) => k + 1);
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ??
        err?.message ??
        "Failed to cleanup catalog";
      setFetchError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setCatalogMaintenanceAction(null);
    }
  }, [currentUser, currentCompany]);

  useEffect(() => {
    if (!isSuperUser) {
      setPageHeaderActions(null);
      return undefined;
    }

    setPageHeaderActions(
      <>
        <Button
          variant="contained"
          color="primary"
          className="jobConfigurationCatalogSeedBtn"
          onClick={handleCatalogSeed}
          disabled={Boolean(catalogMaintenanceAction) || saving}
        >
          {catalogMaintenanceAction === "seed" ? "Seeding…" : "Seed"}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          className="jobConfigurationCatalogCleanupBtn"
          onClick={handleCatalogCleanup}
          disabled={Boolean(catalogMaintenanceAction) || saving}
        >
          {catalogMaintenanceAction === "cleanup" ? "Cleaning up…" : "Cleanup"}
        </Button>
      </>,
    );

    return () => setPageHeaderActions(null);
  }, [
    isSuperUser,
    catalogMaintenanceAction,
    saving,
    handleCatalogSeed,
    handleCatalogCleanup,
    setPageHeaderActions,
  ]);

  const buildWellsPayloadForPad = useCallback(
    (pad) => {
      const newWells = newWellsByPadId[pad.id] || [];
      const serverWells = (pad._wells || []).map((well) => mergeWellRow(well));
      return [...newWells, ...serverWells].map((well) => wellToPayload(well));
    },
    [newWellsByPadId, mergeWellRow],
  );

  const padHasPendingChanges = useCallback(
    (pad) => {
      const padId = pad.id;
      if (String(padId).startsWith("new-")) return true;
      if (editsByEntity.pads?.[padId]) return true;
      if ((newWellsByPadId[padId] || []).length > 0) return true;
      return (pad._wells || []).some((well) => editsWellsById[well.id]);
    },
    [editsByEntity, newWellsByPadId, editsWellsById],
  );

  const collectPadsUpsertPayload = useCallback(() => {
    const payloads = [];
    const processedPadIds = new Set();

    for (const pad of newRowsByEntity.pads || []) {
      payloads.push(
        padWithIncludesToUpsertPayload(pad, buildWellsPayloadForPad(pad)),
      );
      processedPadIds.add(pad.id);
    }

    for (const padId of Object.keys(editsByEntity.pads || {})) {
      if (processedPadIds.has(padId)) continue;
      const original = getOriginalEntityRow("pads", padId);
      if (!original) continue;
      const mergedPad = mergeEntityRow("pads", original);
      payloads.push(
        padWithIncludesToUpsertPayload(
          mergedPad,
          buildWellsPayloadForPad(original),
        ),
      );
      processedPadIds.add(padId);
    }

    for (const pad of tableDataByEntity.pads || []) {
      if (processedPadIds.has(pad.id)) continue;
      if (!padHasPendingChanges(pad)) continue;
      const mergedPad = mergeEntityRow("pads", pad);
      payloads.push(
        padWithIncludesToUpsertPayload(mergedPad, buildWellsPayloadForPad(pad)),
      );
      processedPadIds.add(pad.id);
    }

    return payloads;
  }, [
    newRowsByEntity,
    editsByEntity,
    tableDataByEntity,
    getOriginalEntityRow,
    mergeEntityRow,
    buildWellsPayloadForPad,
    padHasPendingChanges,
  ]);

  const collectEntityUpsertPayload = useCallback(
    (entityKey) => {
      const rows = [];
      const processedIds = new Set();

      for (const row of newRowsByEntity[entityKey] || []) {
        rows.push(entityRowToUpsertPayload(row, entityKey));
        processedIds.add(row.id);
      }

      for (const rowId of Object.keys(editsByEntity[entityKey] || {})) {
        if (processedIds.has(rowId)) continue;
        const original = getOriginalEntityRow(entityKey, rowId);
        if (!original) continue;
        rows.push(entityRowToUpsertPayload(mergeEntityRow(entityKey, original), entityKey));
        processedIds.add(rowId);
      }

      return rows;
    },
    [newRowsByEntity, editsByEntity, getOriginalEntityRow, mergeEntityRow],
  );

  const clearEntityPendingState = useCallback(
    (entityKey) => {
      setNewRowsByEntity((prev) => ({ ...prev, [entityKey]: [] }));
      setEditsByEntity((prev) => {
        const { [entityKey]: _, ...rest } = prev;
        return rest;
      });
      if (entityKey === "pads") {
        setNewWellsByPadId({});
        setEditsWellsById({});
      }
    },
    [],
  );

  const handleCancelPending = useCallback(() => {
    clearEntityPendingState(entity);
    setEditingCell({ rowId: null, columnKey: null, originalValue: null });
    closeAreaPopover();
    closeCrewPopover();
    closeClientPopover();
    closeAreaPopoverForPad();
    closeBooleanPopover();
    closeDatePickerPopover();
  }, [
    entity,
    clearEntityPendingState,
    closeAreaPopover,
    closeCrewPopover,
    closeClientPopover,
    closeAreaPopoverForPad,
    closeBooleanPopover,
    closeDatePickerPopover,
  ]);

  const getCatalogSaveSuccessMessage = useCallback(
    (entityKey) => {
      if (entityKey === "pads") {
        const createdPads = newRowsByEntity.pads?.length ?? 0;
        const updatedPads = Object.keys(editsByEntity.pads || {}).length;
        const createdWells = Object.values(newWellsByPadId).flat().length;
        const updatedWells = Object.keys(editsWellsById).length;
        const padsUpdatedViaWellsOnly = (tableDataByEntity.pads || []).filter(
          (pad) =>
            !String(pad.id).startsWith("new-") &&
            !editsByEntity.pads?.[pad.id] &&
            ((newWellsByPadId[pad.id] || []).length > 0 ||
              (pad._wells || []).some((well) => editsWellsById[well.id])),
        ).length;

        return buildCatalogSaveSuccessMessage("pads", {
          created: createdPads,
          updated: updatedPads + padsUpdatedViaWellsOnly,
          createdWells,
          updatedWells,
        });
      }

      if (usesBatchUpsert(entityKey)) {
        return buildCatalogSaveSuccessMessage(entityKey, {
          created: newRowsByEntity[entityKey]?.length ?? 0,
          updated: Object.keys(editsByEntity[entityKey] || {}).length,
        });
      }

      return buildCatalogSaveSuccessMessage(entityKey, {
        created: newRowsByEntity[entityKey]?.length ?? 0,
        updated: 0,
      });
    },
    [
      newRowsByEntity,
      editsByEntity,
      newWellsByPadId,
      editsWellsById,
      tableDataByEntity,
    ],
  );

  const handleSaveAll = useCallback(async () => {
    if (!hasPendingChanges) return;
    const successMessage = getCatalogSaveSuccessMessage(entity);
    setSaving(true);
    setFetchError(null);
    try {
      if (entity === "pads") {
        const payload = collectPadsUpsertPayload();
        if (payload.length > 0) {
          await DmsService.upsertCatalogPadsWithIncludes(
            payload,
            currentCompany?.id,
          );
        }
        clearEntityPendingState("pads");
      } else if (usesBatchUpsert(entity)) {
        const payload = collectEntityUpsertPayload(entity);
        if (payload.length > 0) {
          await DmsService.upsertCatalog(entity, payload, currentCompany?.id);
        }
        clearEntityPendingState(entity);
      } else {
        const newRows = newRowsByEntity[entity] ?? [];
        if (newRows.length > 0) {
          const rowsPayload = newRows.map((row) => rowToPayload(row, entity));
          await DmsService.postCatalog(entity, rowsPayload, currentCompany?.id);
          setNewRowsByEntity((prev) => ({ ...prev, [entity]: [] }));
        }
      }
      setPageNumber(1);
      setRefreshKey((k) => k + 1);
      toast.success(successMessage);
    } catch (err) {
      setFetchError(
        err?.response?.data?.message ?? err?.message ?? "Failed to save",
      );
    } finally {
      setSaving(false);
    }
  }, [
    hasPendingChanges,
    entity,
    getCatalogSaveSuccessMessage,
    collectPadsUpsertPayload,
    collectEntityUpsertPayload,
    clearEntityPendingState,
    newRowsByEntity,
    currentCompany,
  ]);

  const onDocumentSelect = useCallback((id) => {
    setSelectedDocumentId(id);
    setPageNumber(1);
  }, []);

  const onSearchChange = useCallback((value) => {
    setSearchTerm(value || "");
    setPageNumber(1);
  }, []);

  const getPadWells = useCallback(
    (pad) => [
      ...(newWellsByPadId[pad.id] || []),
      ...(pad._wells || []).map((well) => mergeWellRow(well)),
    ],
    [newWellsByPadId, mergeWellRow],
  );

  const handleWellCellBlur = useCallback(
    (well, columnKey, value) => {
      if (!well) return;
      applyWellEdit(well.id, { [columnKey]: value });
    },
    [applyWellEdit],
  );

  const renderWellEditableTextCell = useCallback(
    (well, col) => {
      const rawValue = well[col.key];
      const displayValue =
        rawValue === null || rawValue === undefined ? "" : String(rawValue);
      const isEditing =
        editingCell.rowId === well.id && editingCell.columnKey === col.key;

      if (!isEditing) {
        return (
          <span
            role="button"
            tabIndex={0}
            className={getCellDisplayClass("pads", well, col.key, "well")}
            onClick={(e) => {
              e.stopPropagation();
              setEditingCell({
                rowId: well.id,
                columnKey: col.key,
                originalValue: displayValue,
              });
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                setEditingCell({
                  rowId: well.id,
                  columnKey: col.key,
                  originalValue: displayValue,
                });
              }
            }}
          >
            {displayValue}
          </span>
        );
      }

      return (
        <TextField
          autoFocus
          variant="outlined"
          size="small"
          fullWidth
          value={displayValue}
          onChange={(e) =>
            handleCellChange(well.id, col.key, e.target.value, "well")
          }
          onClick={(e) => e.stopPropagation()}
          onBlur={(e) => {
            const nextValue = e.target.value;
            setEditingCell({
              rowId: null,
              columnKey: null,
              originalValue: null,
            });
            handleWellCellBlur(well, col.key, nextValue);
          }}
        />
      );
    },
    [editingCell, handleCellChange, handleWellCellBlur, getCellDisplayClass],
  );

  const renderEditableTextCell = useCallback(
    (row, col) => {
      const rawValue = row[col.key];
      const displayValue =
        rawValue === null || rawValue === undefined ? "" : String(rawValue);

      if (isReadOnlyColumn(entity, col.key)) {
        return (
          <span className="jobConfigurationCellValue">{displayValue}</span>
        );
      }

      const isEditing =
        editingCell.rowId === row.id && editingCell.columnKey === col.key;

      if (!isEditing) {
        return (
          <span
            role="button"
            tabIndex={0}
            className={getCellDisplayClass(entity, row, col.key)}
            onClick={() =>
              setEditingCell({
                rowId: row.id,
                columnKey: col.key,
                originalValue: displayValue,
              })
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setEditingCell({
                  rowId: row.id,
                  columnKey: col.key,
                  originalValue: displayValue,
                });
              }
            }}
          >
            {displayValue}
          </span>
        );
      }

      return (
        <TextField
          autoFocus
          variant="outlined"
          size="small"
          fullWidth
          value={displayValue}
          onChange={(e) =>
            handleCellChange(row.id, col.key, e.target.value)
          }
          onBlur={(e) => {
            const { originalValue } = editingCell;
            const nextValue = e.target.value;
            setEditingCell({
              rowId: null,
              columnKey: null,
              originalValue: null,
            });
            handleCellBlur(row, col.key, nextValue, originalValue);
          }}
        />
      );
    },
    [entity, editingCell, handleCellChange, handleCellBlur, getCellDisplayClass],
  );

  return (
    <Fragment>
      <JsonEditorModal
        open={parametersJsonModal.open}
        title="Parameters Json"
        value={parametersJsonModalValue}
        onClose={closeParametersJsonModal}
        onSave={handleParametersJsonSave}
      />
      <div className="jobConfigurationDetail">
          {selectedDocument && (
            <>
              <Tabs
                indicatorColor="primary"
                textColor="primary"
                className="pmTabs mb15"
                value={selectedDocumentId}
                onChange={(e, value) => onDocumentSelect(value)}
                TabIndicatorProps={{ style: { display: "none" } }}
              >
                {visibleTabs.map((doc) => (
                  <Tab
                    key={doc.id}
                    disableRipple
                    value={doc.id}
                    label={doc.name}
                    className={selectedDocumentId === doc.id ? "activeSubTitle" : undefined}
                  />
                ))}
              </Tabs>

              <div className="pageCard subscriptionCard jobConfigurationTableCard jobConfigurationTableCardWithLoader">
                <div className="pageBtnSection pt0 mt5 unitSettingsPageToolbar jobConfigurationSearchSection">
                <div className="searchHolder">
                    <div
                      className="inputHolder"
                      style={{ display: "flex", verticalAlign: "center" }}
                    >
                      <SearchInput
                        search={searchTerm}
                        onChange={onSearchChange}
                      />
                    </div>
                </div>
                  <div className="jobConfigurationActions">
                  {hasPendingChanges && (
                    <>
                      <Button
                        variant="outlined"
                        className="jobConfigurationCancelBtn"
                        onClick={handleCancelPending}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        className="jobConfigurationSaveBtn"
                        onClick={handleSaveAll}
                        disabled={saving}
                      >
                        {saving ? "Saving…" : "Save"}
                      </Button>
                    </>
                  )}
                  <Button
                    className="asideNavItem jobConfigurationAddBtn"
                    startIcon={<MyIcons icon="addCircleOutlineIcon" />}
                    onClick={handleAddRow}
                  >
                    Add
                  </Button>
                </div>
              </div>
                <div className="tableHolder" style={{ position: "relative", minHeight: 120 }}>
                  <table
                    className={`unitSettingsTable${entity === "pads" ? " jobConfigurationPadsTable" : ""}`}
                  >
                    {entity === "pads" && (
                      <colgroup>
                        <col className="jobConfigurationExpandCol" />
                        {columns.map((col) => (
                          <col
                            key={col.key}
                            className={
                              col.key === "name" ? "jobConfigurationNameCol" : undefined
                            }
                          />
                        ))}
                        <col className="actionTableCell" />
                      </colgroup>
                    )}
                    <thead>
                      <tr className="headingRow">
                        {entity === "pads" && (
                          <th className="jobConfigurationExpandCol" />
                        )}
                        {columns.map((col) => (
                          <th
                            key={col.key}
                            className={
                              col.key === "name"
                                ? "jobConfigurationNameCol"
                                : ""
                            }
                          >
                            {col.label}
                          </th>
                        ))}
                        {entity !== "tables" && (
                          <th className="actionTableCell" />
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {entity === "pads"
                        ? paginatedRows.map((row) => {
                            const isExpanded = expandedPadIds.includes(row.id);
                            const padWells = getPadWells(row);

                            return (
                              <Fragment key={row.id}>
                                <tr
                                  className={
                                    isExpanded ? "jobConfigurationPadRowExpanded" : ""
                                  }
                                >
                                  <td
                                    className="jobConfigurationExpandCol"
                                    onClick={() => togglePadExpanded(row.id)}
                                  >
                                    <span
                                      className={`jobConfigurationPadToggle ${isExpanded ? "expanded" : ""}`.trim()}
                                    />
                                  </td>
                                  {columns.map((col) => (
                                    <td
                                      key={col.key}
                                      className={`jobConfigurationTableCell ${col.key === "name" ? "jobConfigurationNameCol" : ""}`.trim()}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {col.key === "areaId" ? (
                                        <span
                                          role="button"
                                          tabIndex={0}
                                          className={getCellDisplayClass("pads", row, "areaId")}
                                          onClick={(e) =>
                                            openAreaPopoverForPad(e, row.id)
                                          }
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                              e.preventDefault();
                                              openAreaPopoverForPad(e, row.id);
                                            }
                                          }}
                                        >
                                          {(() => {
                                            const v = row[col.key];
                                            if (v === "" || v == null) return "Select area";
                                            const a = (catalogAreas || []).find(
                                              (area) => String(area.id) === String(v),
                                            );
                                            return a ? a.name : "Select area";
                                          })()}
                                        </span>
                                      ) : (
                                        renderEditableTextCell(row, col)
                                      )}
                                    </td>
                                  ))}
                                  <td
                                    className="actionTableCell"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="jobConfigurationRowActions">
                                      <IconButton
                                        size="small"
                                        className="jobConfigurationAddWellBtn"
                                        onClick={(e) => handleAddWellToPad(row.id, e)}
                                        aria-label="Add well"
                                      >
                                        <MyIcons icon="addCircleOutlineIcon" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        className="deleteTableItemBtn"
                                        onClick={() => handleRemoveRow(row.id, "entity")}
                                        aria-label="Delete row"
                                      >
                                        <RemoveIcon />
                                      </IconButton>
                                    </div>
                                  </td>
                                </tr>
                                {isExpanded && (
                                  <tr className="jobConfigurationInnerTableHolder">
                                    <td
                                      className="jobConfigurationInnerTableContent"
                                      colSpan={columns.length + 2}
                                    >
                                      <table className="jobConfigurationInnerTable">
                                        <colgroup>
                                          <col className="jobConfigurationExpandCol" />
                                          <col className="jobConfigurationNameCol" />
                                          {PAD_INNER_WELL_COLUMNS.slice(1).map((wellColumn) => (
                                            <col key={wellColumn.key} />
                                          ))}
                                          <col className="actionTableCell" />
                                        </colgroup>
                                        <thead>
                                          <tr>
                                            <th className="jobConfigurationExpandCol" />
                                            {PAD_INNER_WELL_COLUMNS.map((wellColumn) => (
                                              <th
                                                key={wellColumn.key}
                                                className={
                                                  wellColumn.key === "name"
                                                    ? "jobConfigurationNameCol"
                                                    : ""
                                                }
                                              >
                                                {wellColumn.label}
                                              </th>
                                            ))}
                                            <th className="actionTableCell" />
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {padWells.length === 0 ? (
                                            <tr>
                                              <td
                                                colSpan={PAD_INNER_WELL_COLUMNS.length + 2}
                                                className="jobConfigurationInnerTableEmpty"
                                              >
                                                No wells
                                              </td>
                                            </tr>
                                          ) : (
                                            padWells.map((well) => (
                                              <tr key={`well-${row.id}-${well.id}`}>
                                                <td className="jobConfigurationExpandCol" />
                                                {PAD_INNER_WELL_COLUMNS.map((wellColumn) => (
                                                  <td
                                                    key={wellColumn.key}
                                                    className={`jobConfigurationTableCell ${wellColumn.key === "name" ? "jobConfigurationNameCol" : ""}`.trim()}
                                                    onClick={(e) => e.stopPropagation()}
                                                  >
                                                    {renderWellEditableTextCell(well, wellColumn)}
                                                  </td>
                                                ))}
                                                <td
                                                  className="actionTableCell"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <IconButton
                                                    size="small"
                                                    className="deleteTableItemBtn"
                                                    onClick={() => handleRemoveRow(well.id, "well")}
                                                    aria-label="Delete well"
                                                  >
                                                    <RemoveIcon />
                                                  </IconButton>
                                                </td>
                                              </tr>
                                            ))
                                          )}
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            );
                          })
                        : paginatedRows.map((row) => (
                            <tr key={row.id}>
                              {columns.map((col) => (
                                <td
                                  key={col.key}
                                  className={`jobConfigurationTableCell ${col.key === "name" ? "jobConfigurationNameCol" : ""} ${isCheckboxColumn(col) ? "jobConfigurationCheckboxCell" : ""}`.trim()}
                                >
                                  {isCheckboxColumn(col) ? (
                                    <Checkbox
                                      color="default"
                                      checked={isTruthyBooleanValue(row[col.key])}
                                      onChange={(e) =>
                                        handleCheckboxChange(
                                          row,
                                          col.key,
                                          e.target.checked,
                                        )
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  ) : isBooleanCell(row[col.key]) ||
                                    isBooleanColumn(col) ? (
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      className={getCellDisplayClass(entity, row, col.key)}
                                      onClick={(e) =>
                                        openBooleanPopover(e, row.id, col.key)
                                      }
                                      onKeyDown={(e) => {
                                        if (
                                          e.key === "Enter" ||
                                          e.key === " "
                                        ) {
                                          e.preventDefault();
                                          openBooleanPopover(
                                            e,
                                            row.id,
                                            col.key,
                                          );
                                        }
                                      }}
                                    >
                                      {String(row[col.key] ?? "")}
                                    </span>
                                  ) : entity === "crews" &&
                                    col.key === "clientId" ? (
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      className={getCellDisplayClass(entity, row, "clientId")}
                                      onClick={(e) =>
                                        openClientPopover(e, row.id)
                                      }
                                      onKeyDown={(e) => {
                                        if (
                                          e.key === "Enter" ||
                                          e.key === " "
                                        ) {
                                          e.preventDefault();
                                          openClientPopover(e, row.id);
                                        }
                                      }}
                                    >
                                      {(() => {
                                        const v = row[col.key];
                                        if (v === "" || v == null)
                                          return "Select client";
                                        const c = (catalogClients || []).find(
                                          (cl) =>
                                            String(cl.id) === String(v),
                                        );
                                        return c ? c.name : "Select client";
                                      })()}
                                    </span>
                                  ) : (
                                    renderEditableTextCell(row, col)
                                  )}
                                </td>
                              ))}
                              {entity !== "tables" && (
                                <td className="actionTableCell">
                                  <IconButton
                                    size="small"
                                    className="deleteTableItemBtn"
                                    onClick={() => handleRemoveRow(row.id)}
                                    aria-label="Delete row"
                                  >
                                    <RemoveIcon />
                                  </IconButton>
                                </td>
                              )}
                            </tr>
                          ))}
                    </tbody>
                  </table>
                </div>
                <div className="jobConfigurationLoader">
                  <Fade in={loading} unmountOnExit>
                    <CircularProgress />
                  </Fade>
                </div>
                {pagination &&
                  (paginatedRows.length > 0 ||
                    pagination.itemsTotalCount > 0) && (
                    <TablePagination
                      pagination={pagination}
                      onPrevPage={onPrevPage}
                      onNextPage={onNextPage}
                      handleRowCount={handleRowCount}
                      loading={loading}
                    />
                  )}
              </div>

              <Popover
                open={Boolean(booleanPopover.anchorEl)}
                anchorEl={booleanPopover.anchorEl}
                onClose={closeBooleanPopover}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  className: "jobConfigurationBooleanPopover",
                  style: booleanPopover.anchorEl
                    ? { width: booleanPopover.anchorEl.offsetWidth, minWidth: booleanPopover.anchorEl.offsetWidth }
                    : undefined,
                }}
              >
                <List dense disablePadding>
                  <ListItem button onClick={() => selectBooleanValue("true")}>
                    <ListItemText primary="True" />
                  </ListItem>
                  <ListItem button onClick={() => selectBooleanValue("false")}>
                    <ListItemText primary="False" />
                  </ListItem>
                </List>
              </Popover>

              <Popover
                open={Boolean(areaPopover.anchorEl)}
                anchorEl={areaPopover.anchorEl}
                onClose={closeAreaPopover}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  className: "jobConfigurationCellPopover",
                  style: areaPopover.anchorEl
                    ? { width: areaPopover.anchorEl.offsetWidth, minWidth: areaPopover.anchorEl.offsetWidth }
                    : undefined,
                }}
              >
                <List dense disablePadding>
                  <ListItem button onClick={() => selectArea(null)}>
                    <ListItemText primary="Select area" />
                  </ListItem>
                  {(catalogAreas || []).map((a) => (
                    <ListItem
                      key={a.id}
                      button
                      onClick={() => selectArea(a)}
                    >
                      <ListItemText primary={a.name} />
                    </ListItem>
                  ))}
                </List>
              </Popover>

              <Popover
                open={Boolean(crewPopover.anchorEl)}
                anchorEl={crewPopover.anchorEl}
                onClose={closeCrewPopover}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  className: "jobConfigurationCellPopover",
                  style: crewPopover.anchorEl
                    ? { width: crewPopover.anchorEl.offsetWidth, minWidth: crewPopover.anchorEl.offsetWidth }
                    : undefined,
                }}
              >
                <List dense disablePadding>
                  <ListItem button onClick={() => selectCrew(null)}>
                    <ListItemText primary="Select crew" />
                  </ListItem>
                  {(catalogCrews || []).map((d) => (
                    <ListItem
                      key={d.id}
                      button
                      onClick={() => selectCrew(d)}
                    >
                      <ListItemText primary={d.name} />
                    </ListItem>
                  ))}
                </List>
              </Popover>

              <Popover
                open={Boolean(clientPopover.anchorEl)}
                anchorEl={clientPopover.anchorEl}
                onClose={closeClientPopover}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  className: "jobConfigurationCellPopover",
                  style: clientPopover.anchorEl
                    ? { width: clientPopover.anchorEl.offsetWidth, minWidth: clientPopover.anchorEl.offsetWidth }
                    : undefined,
                }}
              >
                <List dense disablePadding>
                  <ListItem button onClick={() => selectClient(null)}>
                    <ListItemText primary="Select client" />
                  </ListItem>
                  {(catalogClients || []).map((c) => (
                    <ListItem key={c.id} button onClick={() => selectClient(c)}>
                      <ListItemText primary={c.name} />
                    </ListItem>
                  ))}
                </List>
              </Popover>

              <Popover
                open={Boolean(areaPopoverForPad.anchorEl)}
                anchorEl={areaPopoverForPad.anchorEl}
                onClose={closeAreaPopoverForPad}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  className: "jobConfigurationCellPopover",
                  style: areaPopoverForPad.anchorEl
                    ? { width: areaPopoverForPad.anchorEl.offsetWidth, minWidth: areaPopoverForPad.anchorEl.offsetWidth }
                    : undefined,
                }}
              >
                <List dense disablePadding>
                  <ListItem button onClick={() => selectAreaForPad(null)}>
                    <ListItemText primary="Select area" />
                  </ListItem>
                  {(catalogAreas || []).map((a) => (
                    <ListItem key={a.id} button onClick={() => selectAreaForPad(a)}>
                      <ListItemText primary={a.name} />
                    </ListItem>
                  ))}
                </List>
              </Popover>

            </>
          )}
      </div>
    </Fragment>
  );
};

export default JobConfiguration;
